import { useColors } from 'legacyfrontend/hooks/useColors';
import { useTemplateConfig } from 'legacyfrontend/hooks/useTemplateConfig';
import {
  selectFooter,
  selectFooterLegalLinks,
  selectFooterUtilLinks,
  selectHeader,
  selectPaisId,
  selectPrincipalOffice,
  selectTemplateData,
} from 'legacyfrontend/redux/selectors/application-selectors';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPhoneAlt } from 'react-icons/fa';
import { MdOutlineMailOutline } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { ROUTE_KEYS } from '../constants';
import AboutUsModal from './footer/AboutUsModal';
import LegalInfoModal from './footer/LegalInfoModal';
import { LogosFooter } from './footer/LogosFooter';
import OtherLinksModal from './footer/OtherLinksModal';
import SocialNetworks from './footer/SocialNetworks';

export default function Footer() {
  const { t } = useTranslation(['layout']);
  const footer = useSelector(selectFooter);
  const legalLinks = useSelector(selectFooterLegalLinks);
  const utilLinks = useSelector(selectFooterUtilLinks);
  const templateData = useSelector(selectTemplateData);
  const header = useSelector(selectHeader);
  const principalOffice = useSelector(selectPrincipalOffice);
  const paisId = useSelector(selectPaisId);
  const [isOtherLinksModalOpen, setIsOtherLinksModalOpen] = useState(false);
  const [isLegalInfoModalOpen, setIsLegalInfoModalOpen] = useState(false);
  const [isAboutUsModalOpen, setIsAboutUsModalOpen] = useState(false);

  const {
    labels,
    legal,
    consumerRightsText,
    claimsBookText,
    consumerRightsLaw,
    showGeneralDirection,
    templateConfig,
  } = useTemplateConfig();

  const { background, text } = useColors();

  const otherLinks = useMemo(() => {
    return [...(legalLinks || []), ...(utilLinks || [])];
  }, [legalLinks, utilLinks]);

  const legalInfoItems = useMemo(() => {
    const items = [];

    if (footer?.razonSocial) {
      items.push(footer.razonSocial);
    }

    if (legal.showCADASTUR && footer?.legajo) {
      items.push(`${labels.registration} ${footer.legajo}`);
    }

    if (legal.showLegajo && footer?.legajo) {
      items.push(`${labels.registration} ${footer.legajo}`);
    }

    if (legal.showRUC && footer?.cuit) {
      items.push(`${labels.taxId} ${footer.cuit}`);
    }

    if (legal.showCUIT && footer?.cuit) {
      items.push(`${labels.taxId} ${footer.cuit}`);
    }

    if (legal.showCNPJ && footer?.cuit) {
      items.push(`${labels.taxId} ${footer.cuit}`);
    }

    return items;
  }, [legal, footer?.legajo, footer?.cuit, footer?.razonSocial, labels]);

  const legalText = useMemo(() => {
    return `${t('footer.consumerRights')} - ${t('footer.legalText')}`;
  }, [t]);

  const aboutUsText = useMemo(() => {
    return (
      footer.detalle?.find((detalle) => detalle.tipoEnlace?.nombre === 'about-us-footer')?.valor ?? undefined
    );
  }, [footer.detalle]);

  if (!footer || templateConfig?.hideFooter) {
    return null;
  }

  return (
    <>
      <footer className='footer-content footer-mb' id='footerContent' style={{ backgroundColor: background, color: text }}>
        {/* Contact Column */}
        <div className='footer-mb__column' id='footerContact'>
          <div className='footer-mb__column__body'>
            <h3 className='footer-mb__column__body__title'>{t('footer.contact', 'Contacto')}</h3>

            {/* About Us */}
            <div className='footer-mb__column__body__content'>
              <div className='footer-mb__column__body__content__links'>
                <button
                  id='aboutUsBtn'
                  onClick={() => setIsAboutUsModalOpen(true)}
                  className='cursor-pointer'
                  style={{ background: 'none', border: 0, color: 'inherit', font: 'inherit', padding: 0, textAlign: 'left' }}
                  type='button'
                >
                  {t('footer.about_us')}
                </button>
                {/* Offices Link */}
                <a
                  href={ROUTE_KEYS.AGENCIES}
                  target='_blank'
                  rel='noreferrer'
                >
                  {t('footer.office')}
                </a>
              </div>
              <div className='footer-mb__column__body__content__info'>
                <div className='footer-mb__column__body__content__info__contact'>
                {/* Email */}
                {header?.emailContacto && (
                  <div className='footer-mb__column__body__content__info__line mb-2'>
                    <a href={`mailto:${header.emailContacto}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none', color: 'inherit' }}>
                      <span><MdOutlineMailOutline className='mx-auto w-5 text-center h-5' /></span>
                      <span style={{ textTransform: 'lowercase' }}>{header.emailContacto}</span>
                    </a>
                  </div>
                )}
                {/* Phone */}
                {header?.telefonoContacto && (
                  <div className='footer-mb__column__body__content__info__line mb-2'>
                    <a href={`tel:${header.telefonoContacto}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none', color: 'inherit' }}>
                      <span><FaPhoneAlt className='mx-auto w-5 text-center h-5' /></span>
                      <span>{header.telefonoContacto}</span>
                    </a>
                  </div>
                )}
                </div>

                {/* Company Info */}
                {legalInfoItems.map((item, index) => (
                  <div key={index}>
                    {item}
                  </div>
                ))}
                {/* Address */}
                {principalOffice?.direccion && (
                  <div className='footer-mb__column__body__content__info__line'>
                    <span><i className='fas fa-map-marker-alt mx-auto w-5 text-center h-5'></i></span>
                    <span>{principalOffice.direccion}</span>
                  </div>
                )}
                {/* Hours */}
                {principalOffice?.horario && (
                  <div className='footer-mb__column__body__content__info__line'>
                    <span><i className='fas fa-clock mx-auto w-5 text-center h-5'></i></span>
                    <span>{principalOffice.horario}</span>
                  </div>
                )}
              </div>
            </div>
            {/* Social Networks */}
            {templateData?.socialNetworks && (
              <div className='footer-mb__column__body__social'>
                <SocialNetworks socialNetworks={templateData.socialNetworks} />
              </div>
            )}
          </div>
          <div className='footer-mb__column__footer'>
            <div
              className='border-t w-full'
              style={{
                borderColor: text,
                opacity: 0.3,
              }}
            />
          </div>
        </div>

        {/* Links Column */}
        <div className='footer-mb__column' id='footerLinks'>
          <div className='footer-mb__column__body'>
            <h3 className='footer-mb__column__body__title'>{t('footer.info', 'Enlaces')}</h3>
            <div className='footer-mb__column__body__content footer-mb__column__body__content__links'>
              {paisId === 2 ? (
                <>
                  {/* Términos y Condiciones */}
                  {templateConfig.showTermsOfUse && (
                    <a
                      href={ROUTE_KEYS.TERMS_AND_CONDITIONS}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {t('footer.useTerms')}
                    </a>
                  )}

                  {/* Libro de Reclamaciones */}
                  {templateConfig.showClaimsBook && (
                    <a
                      href={ROUTE_KEYS.CLAIMS}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {claimsBookText || t('footer.claimsBook')}
                    </a>
                  )}

                  {/* Formas de pago */}
                  {templateConfig.showPaymentMethods && (
                    <a
                      href={ROUTE_KEYS.PAY_METHODS}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {t('footer.pay_methods')}
                    </a>
                  )}

                  {/* Política de Privacidad */}
                  {templateConfig.showPrivacyPolicy && (
                    <a
                      href={ROUTE_KEYS.PRIVACY_POLICY}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {t('footer.privacyPolicy')}
                    </a>
                  )}

                  {/* Other Links */}
                  {otherLinks.length > 0 && (
                    <a
                      onClick={() => setIsOtherLinksModalOpen(true)}
                      rel='noreferrer'
                      className='cursor-pointer'
                    >
                      {t('footer.other_links')}
                    </a>
                  )}
                </>
              ) : paisId === 4 ? (
                <>
                  {/* Términos y Condiciones */}
                  {templateConfig.showTermsOfUse && (
                    <a
                      href={ROUTE_KEYS.TERMS_AND_CONDITIONS}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {t('footer.useTerms')}
                    </a>
                  )}

                  {/* Formas de pago */}
                  {templateConfig.showPaymentMethods && (
                    <a
                      href={ROUTE_KEYS.PAY_METHODS}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {t('footer.pay_methods')}
                    </a>
                  )}

                  {/* Política de Privacidad (México: página dedicada) */}
                  {templateConfig.showPrivacyPolicy && (
                    <a
                      href={ROUTE_KEYS.PRIVACY_POLICY}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {t('footer.privacyPolicy')}
                    </a>
                  )}

                  {/* Condiciones generales (México: página dedicada) */}
                  {templateConfig.showGeneralContractingConditions && (
                    <a
                      href={ROUTE_KEYS.GENERAL_CONDITIONS}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {t('footer.generalConditions')}
                    </a>
                  )}

                  {/* Other Links */}
                  {otherLinks.length > 0 && (
                    <a
                      onClick={() => setIsOtherLinksModalOpen(true)}
                      rel='noreferrer'
                      className='cursor-pointer'
                    >
                      {t('footer.other_links')}
                    </a>
                  )}
                </>
              ) : (
                <>
                  {templateConfig.showTermsOfUse && (
                    <a
                      href={ROUTE_KEYS.TERMS_AND_CONDITIONS}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {t('footer.useTerms')}
                    </a>
                  )}

                  {/* Payment Methods */}
                  {templateConfig.showPaymentMethods && (
                    <a
                      href={ROUTE_KEYS.PAY_METHODS}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {t('footer.pay_methods')}
                    </a>
                  )}

                  {/* Condiciones generales (Argentina) */}
                  {templateConfig.showGeneralContractingConditions && (
                    <a
                      href={ROUTE_KEYS.GENERAL_CONDITIONS}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {t('footer.generalConditions')}
                    </a>
                  )}

                  {/* Política de Privacidad (Brasil) */}
                  {templateConfig.showPrivacyPolicy && (
                    <a
                      href={ROUTE_KEYS.PRIVACY_POLICY}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {t('footer.privacyPolicy')}
                    </a>
                  )}

                  {/* Other Links */}
                  {otherLinks.length > 0 && (
                    <a
                      onClick={() => setIsOtherLinksModalOpen(true)}
                      rel='noreferrer'
                      className='cursor-pointer'
                    >
                      {t('footer.other_links')}
                    </a>
                  )}

                  {/* Legal Information */}
                  {templateConfig.showConsumerRights && (
                    <a
                      onClick={() => setIsLegalInfoModalOpen(true)}
                      rel='noreferrer'
                      className='cursor-pointer'
                    >
                      Información Legal
                    </a>
                  )}

                  {/* Withdrawal Button */}
                  {templateConfig.showConsumerRights && consumerRightsText && (
                    <a
                      href={t('footer.link-reclamations')}
                      rel='noreferrer'
                      target='_blank'
                    >
                      {consumerRightsText}
                    </a>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Logos row - aligned at bottom */}
          <div className='footer-mb__column__footer'>
            <LogosFooter />
          </div>
        </div>
      </footer>

      {/* Other Links Modal */}
      {otherLinks.length > 0 && (
        <OtherLinksModal
          open={isOtherLinksModalOpen}
          onClose={() => setIsOtherLinksModalOpen(false)}
          links={otherLinks}
        />
      )}

      {/* Legal Info Modal */}
      {templateConfig.showConsumerRights && (
        <LegalInfoModal
          open={isLegalInfoModalOpen}
          onClose={() => setIsLegalInfoModalOpen(false)}
          legalText={legalText}
          consumerRightsLaw={consumerRightsLaw}
          showGeneralDirection={showGeneralDirection}
        />
      )}

      {/* About Us Modal */}
      <AboutUsModal
        open={isAboutUsModalOpen}
        onClose={() => setIsAboutUsModalOpen(false)}
        aboutUsText={aboutUsText}
      />
    </>
  );
}
